import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { Modal } from 'react-bootstrap-v5';
import Swal from 'sweetalert2';
import { IContaBancaria, ITrilhaPagina, ITrilhaRegistro, ITrilhaRelatorio } from '../../interfaces/IModuloBancario';
import ModuloBancarioService from '../../services/ModuloBancarioService';

interface Props {
    esteiraId: number;
    /** "AAAA-MM"; vazio = todas. */
    competencia: string;
    contas: IContaBancaria[];
}

const ACAO: Record<string, string> = {
    CONTA_BANCARIA_CRIADA: 'Conta bancária criada',
    CONTA_BANCARIA_ALTERADA: 'Conta bancária alterada',
    EXTRATO_REGISTRADO: 'Extrato registrado',
    EXTRATO_CONTA_VINCULADA: 'Conta vinculada ao extrato',
    EXTRATO_SALDO_INFORMADO: 'Saldo informado',
    EXTRATO_ACEITO: 'Extrato aceito',
    EXTRATO_RETIDO: 'Extrato retido',
    EXTRATO_DESCARTADO: 'Extrato descartado',
    EXTRATO_EXCLUIDO: 'Extrato excluído',
    SETUP_CATEGORIA_CRIADA: 'Categoria criada',
    SETUP_CATEGORIA_RENOMEADA: 'Categoria renomeada',
    SETUP_VERSAO_APROVADA: 'Versão do Setup aprovada',
    SETUP_CATEGORIA_INATIVADA: 'Categoria inativada',
    SETUP_CATEGORIA_REATIVADA: 'Categoria reativada',
    MINUTAS_GERADAS: 'Minutas geradas',
    MINUTA_CANDIDATO_ESCOLHIDO: 'Candidato escolhido',
    MINUTA_APROVADA: 'Minuta aprovada',
    MINUTAS_APROVADAS_LOTE: 'Minutas aprovadas em lote',
    MINUTA_REPROVADA: 'Minuta reprovada',
    MINUTA_REPROVACAO_DESFEITA: 'Reprovação desfeita',
    MINUTA_DESAPROVADA: 'Minuta desaprovada',
    FECHAMENTO_REALIZADO: 'Conta fechada',
    FECHAMENTO_DESFEITO: 'Fechamento desfeito',
    FECHAMENTO_EXPORTADO: 'Arquivo exportado',
    FECHAMENTO_REEMITIDO: 'Arquivo reemitido',
    FECHAMENTO_REABERTO: 'Conta reaberta',
    RETORNO_DOMINIO_INFORMADO: 'Retorno do Domínio',
    MINUTAS_CONTABILIZADAS: 'Minutas contabilizadas',
    MODULO_MIGRADO_ESTEIRA: 'Módulo levado à esteira nova',
    MINUTAS_REMOVIDAS_REIMPORTACAO: 'Minutas removidas na reimportação',
    COMPROVANTES_LIDOS: 'Comprovantes lidos',
    COMPROVANTE_ANEXADO: 'Comprovante anexado',
    COMPROVANTE_DESANEXADO: 'Comprovante retirado',
    COMPROVANTE_EXCLUIDO: 'Comprovante excluído',
    COMPROVANTES_DISPENSADOS: 'Comprovantes dispensados',
    PAGAMENTO_RFB_LIDO: 'Relatório RFB lido',
    PAGAMENTO_RFB_DESCARTADO: 'Relatório RFB descartado',
    PAGAMENTO_RFB_DISPENSADO: 'Relatório RFB dispensado',
    PAGAMENTO_RFB_CONTA_ALTERADA: 'Conta de código de receita alterada',
    NFSE_LIDA: 'XML de NFS-e lido',
    NFSE_DESCARTADA: 'XML de NFS-e descartado',
    NFSE_VINCULADA: 'Provisão vinculada à NFS-e',
    NFSE_POLITICA_ALTERADA: 'Provisão pelo líquido alterada',
    FOLHA_LIDA: 'Folha analítica lida',
    FOLHA_DESCARTADA: 'Folha analítica descartada',
    FOLHA_VINCULADA: 'Débito vinculado à folha',
    FOLHA_CALENDARIO_ALTERADO: 'Calendário da folha alterado',
    RETORNO_COBRANCA_LIDO: 'Retorno de cobrança lido',
    RETORNO_COBRANCA_DESCARTADO: 'Retorno de cobrança descartado',
    RETORNO_TITULO_VINCULADO: 'Título vinculado a provisão',
    RETORNO_COBRANCA_DISPENSADO: 'Retorno de cobrança dispensado',
    MINUTA_AUTOAPROVADA: 'Minuta autoaprovada',
    MINUTA_REVISADA: 'Minuta autoaprovada revisada',
    POLITICA_AUTOAPROVACAO_ALTERADA: 'Política de autoaprovação alterada',
    TRILHA_PDF_EMITIDO: 'Relatório da trilha emitido',
};

const INTERVALO_MS = 5000;

const CADEIA: Record<string, string> = {
    INTEGRA: 'cadeia íntegra',
    INTEGRA_COM_SIMULTANEOS: 'cadeia íntegra, com gravações simultâneas',
    QUEBRADA: 'CADEIA QUEBRADA',
};

const brl = (valor: number | null) =>
    valor === null ? '—' : valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const descreverConta = (c?: IContaBancaria) =>
    c ? `${c.banco} / ${c.agencia ? `${c.agencia} / ` : ''}${c.conta}${c.descricao ? ` — ${c.descricao}` : ''}` : '—';

function detalheFormatado(json: string | null): string {
    if (!json) return '';
    try {
        return JSON.stringify(JSON.parse(json), null, 2);
    } catch {
        return json;
    }
}

// DEVN3-770 — consulta da trilha da empresa. Só leitura: a API não tem como alterar nem apagar registro.
export default function TrilhaBancaria({ esteiraId, competencia, contas }: Props) {
    const aberta = true;
    const [pagina, setPagina] = useState(1);
    const [filtroConta, setFiltroConta] = useState(0);
    const [filtroAcao, setFiltroAcao] = useState('');
    const [dados, setDados] = useState<ITrilhaPagina | null>(null);
    const [carregando, setCarregando] = useState(false);
    const [detalhe, setDetalhe] = useState<ITrilhaRegistro | null>(null);
    const [relatorios, setRelatorios] = useState<ITrilhaRelatorio[]>([]);
    const ultimaBusca = useRef(0);

    const buscar = useCallback(async () => {
        const busca = ++ultimaBusca.current;
        setCarregando(true);
        try {
            const resposta = await ModuloBancarioService.trilha(esteiraId, {
                contaBancariaId: filtroConta || undefined,
                competencia: competencia || undefined,
                acao: filtroAcao || undefined,
                pagina,
            });
            // Trocar o filtro dispara duas buscas (a página volta para 1): vale só a última.
            if (busca === ultimaBusca.current) setDados(resposta);
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? 'Não foi possível carregar a trilha.', icon: 'error' });
        } finally {
            setCarregando(false);
        }
    }, [esteiraId, filtroConta, competencia, filtroAcao, pagina]);

    useEffect(() => {
        if (aberta) buscar();
    }, [aberta, buscar]);

    // Filtro novo começa da primeira página.
    useEffect(() => { setPagina(1); }, [esteiraId, filtroConta, competencia, filtroAcao]);

    const baixarCsv = () =>
        ModuloBancarioService.baixarTrilhaCsv(esteiraId, { contaBancariaId: filtroConta || undefined, competencia: competencia || undefined },
            `trilha_bancaria${competencia ? `_${competencia}` : ''}.csv`)
            .catch((err: any) => Swal.fire({ text: err?.message ?? 'Não foi possível gerar o CSV.', icon: 'error' }));

    // DEVN3-779 — o PDF é por conta e competência; a geração roda no Worker.
    const podeEmitir = filtroConta > 0 && !!competencia;

    const buscarRelatorios = useCallback(async () => {
        if (!podeEmitir) {
            setRelatorios([]);
            return;
        }
        try {
            setRelatorios(await ModuloBancarioService.relatoriosTrilha(esteiraId, filtroConta, competencia));
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? 'Não foi possível carregar os relatórios.', icon: 'error' });
        }
    }, [esteiraId, filtroConta, competencia, podeEmitir]);

    useEffect(() => {
        if (aberta) buscarRelatorios();
    }, [aberta, buscarRelatorios]);

    const gerando = relatorios.some(r => r.situacao === 'PROCESSANDO');

    useEffect(() => {
        if (!aberta || !gerando) return;
        const timer = setInterval(buscarRelatorios, INTERVALO_MS);
        return () => clearInterval(timer);
    }, [aberta, gerando, buscarRelatorios]);

    // Cada emissão concluída é um registro novo na trilha.
    const emitidos = relatorios.filter(r => r.situacao === 'PRONTO').length;

    useEffect(() => {
        if (aberta && emitidos > 0) buscar();
    }, [aberta, emitidos, buscar]);

    const emitirPdf = async () => {
        try {
            await ModuloBancarioService.solicitarTrilhaPdf(esteiraId, filtroConta, competencia);
            await buscarRelatorios();
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? 'Não foi possível gerar o PDF.', icon: 'error' });
        }
    };

    const baixarRelatorio = (r: ITrilhaRelatorio) =>
        ModuloBancarioService.baixarRelatorioTrilha(esteiraId, r.id, `trilha_bancaria_conta${r.contaBancariaId}_${r.competencia}.pdf`)
            .catch((err: any) => Swal.fire({ text: err?.message ?? 'Não foi possível baixar o PDF.', icon: 'error' }));

    const totalPaginas = dados ?Math.max(1, Math.ceil(dados.total / dados.tamanhoPagina)) : 1;

    return (
        <div className='card card-flush mb-4'>
            <div className='card-header align-items-center'>
                <h3 className='card-title fw-bold'>
                    Trilha de auditoria
                    {carregando && <span className='spinner-border spinner-border-sm ms-3'></span>}
                </h3>
                {aberta && (
                    <div className='d-flex flex-wrap align-items-center gap-2' onClick={e => e.stopPropagation()}>
                        <select className='form-select form-select-sm' style={{ width: 240 }} value={filtroConta} onChange={e => setFiltroConta(Number(e.target.value))}>
                            <option value={0}>Todas as contas bancárias</option>
                            {contas.map(c => <option key={c.id} value={c.id}>{descreverConta(c)}</option>)}
                        </select>
                        <select className='form-select form-select-sm' style={{ width: 220 }} value={filtroAcao} onChange={e => setFiltroAcao(e.target.value)}>
                            <option value=''>Todas as ações</option>
                            {Object.entries(ACAO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        <button className='btn btn-sm btn-light' onClick={baixarCsv} title='Conta bancária e competência escolhidas'>CSV</button>
                        <button className='btn btn-sm btn-light' disabled={!podeEmitir || gerando} onClick={emitirPdf}
                            title={podeEmitir ? 'Relatório da conta bancária e competência escolhidas' : 'Escolha a conta bancária e a competência'}>PDF</button>
                    </div>
                )}
            </div>
            {aberta && (
                <div className='card-body pt-0'>
                    <div className='text-muted fs-7 mb-3'>
                        Registros da empresa, sem edição nem exclusão. {competencia
                            ? `Filtrado pela competência ${moment(competencia, 'YYYY-MM').format('MM/YYYY')}: eventos de extrato, conta e Setup não têm competência e só aparecem em "Todas".`
                            : 'Escolha a competência no topo da página para filtrar as minutas.'}
                    </div>
                    {relatorios.length > 0 && (
                        <div className='mb-3'>
                            <div className='fw-bold fs-7 mb-1'>Relatórios em PDF desta conta e competência</div>
                            {relatorios.map(r => (
                                <div key={r.id} className='d-flex align-items-center gap-2 fs-7 mb-1'>
                                    <span className='text-muted'>{moment.utc(r.criadoEm).local().format('DD/MM/YYYY HH:mm')} · {r.usuarioNome}</span>
                                    {r.situacao === 'PROCESSANDO' && <><span className='spinner-border spinner-border-sm'></span> Gerando...</>}
                                    {r.situacao === 'ERRO' && <span className='text-danger'>{r.mensagem ?? 'Falha ao gerar.'}</span>}
                                    {r.situacao === 'PRONTO' && (
                                        <>
                                            <span className={clsx(r.resultadoCadeia === 'QUEBRADA' && 'text-danger fw-bold')}>{r.resultadoCadeia ? CADEIA[r.resultadoCadeia] : ''}</span>
                                            <button className='btn btn-sm btn-light py-1' onClick={() => baixarRelatorio(r)}>Baixar</button>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {dados && dados.registros.length === 0 ? (
                        !carregando && <div className='text-muted'>Nenhum registro.</div>
                    ) : dados && (
                        <>
                            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
                                <thead>
                                    <tr className='fw-bold text-muted'>
                                        <th>Data</th>
                                        <th>Ação</th>
                                        <th>Autor</th>
                                        <th>Conta bancária</th>
                                        <th className='text-end'>Valor</th>
                                        <th>Motivo</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dados.registros.map(r => (
                                        <tr key={r.id}>
                                            {/* O backend grava em UTC sem o "Z". */}
                                            <td className='text-nowrap fs-7'>{moment.utc(r.dataEvento).local().format('DD/MM/YYYY HH:mm')}</td>
                                            <td className='fs-7'>{ACAO[r.acao] ?? r.acao}</td>
                                            <td className='fs-7'>{r.usuarioNome ?? 'SYSTEM'}</td>
                                            <td className='fs-7'>{r.contaBancariaId ? descreverConta(contas.find(c => c.id === r.contaBancariaId)) : '—'}</td>
                                            <td className='text-end text-nowrap fs-7'>{brl(r.valor)}</td>
                                            <td className='fs-7' style={{ maxWidth: 260 }}>{r.motivo ?? ''}</td>
                                            <td className='text-end'>
                                                {r.detalheJson && (
                                                    <button className='btn btn-sm btn-icon btn-light' title='Detalhe' onClick={() => setDetalhe(r)}>
                                                        <i className='fas fa-search'></i>
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className='d-flex align-items-center justify-content-end gap-2'>
                                <span className='text-muted fs-7'>{dados.total} registro(s) · página {dados.pagina} de {totalPaginas}</span>
                                <button className='btn btn-sm btn-light' disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}>Anterior</button>
                                <button className='btn btn-sm btn-light' disabled={pagina >= totalPaginas} onClick={() => setPagina(p => p + 1)}>Próxima</button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {detalhe && (
                <Modal show onHide={() => setDetalhe(null)} centered size='lg'>
                    <Modal.Header closeButton>
                        <Modal.Title>{ACAO[detalhe.acao] ?? detalhe.acao}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <pre className='fs-7 mb-0' style={{ maxHeight: 480, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{detalheFormatado(detalhe.detalheJson)}</pre>
                    </Modal.Body>
                </Modal>
            )}
        </div>
    );
}
